/**
 * The practice's lens dispensing job list, opened from the nav bar.
 *
 * Every dispensing job across the practice's patients - glasses and contact lenses - split into
 * active and collected like the task list, read from the API each time it opens.
 */
app.controller("lensDispensingJobListController", [
	"$scope",
	"$mdDialog",
	"$timeout",
	"$state",
	"$q",
	"LensDispensingStore",
	"LensPrescribingStore",
	"LensStatusDialog",
	"CurrentPatient",
	"PatientAPI",
	"toast",
	function ($scope, $mdDialog, $timeout, $state, $q, Store, Rx, LensStatusDialog, CurrentPatient, PatientAPI, toast) {
		$scope.S = Store;
		$scope.R = Rx;
		$scope.CP = CurrentPatient;
		$scope.$mdDialog = $mdDialog;

		// Stages a job can be filtered to on the active tab - everything short of collected.
		$scope.stages = Store.STATUSES.filter((s) => s.key !== "COLLECTED");
		$scope.filters = { tab: "active", type: "all", status: null, overdue: false, search: "" };
		$scope.types = [
			{ key: "all", label: "All" },
			{ key: "glasses", label: "Glasses", icon: "fa-solid fa-glasses" },
			{ key: "contacts", label: "Contacts", icon: "fa-solid fa-eye" },
		];
		$scope.sort = { key: "status", reverse: true };
		$scope.rows = [];
		$scope.visible = [];

		const isActive = (row) => row.job.status !== "COLLECTED";
		const typeOf = (row) => (Store.isContacts(row.job) ? "contacts" : "glasses");
		const ofType = (row) => $scope.filters.type === "all" || typeOf(row) === $scope.filters.type;
		// The rows the tab and stage counts are taken from: the pathway filter, before the rest.
		const typed = () => $scope.rows.filter(ofType);

		$scope.patientName = (patient) => [patient.firstName, patient.lastName].filter(Boolean).join(" ") || "Unknown patient";
		$scope.statusClass = (job) => "status-" + job.status.toLowerCase().replace("_", "-");
		$scope.total = (job) => Store.totals(job).total;
		$scope.nextAction = Store.nextActionFor;
		$scope.state = { loading: true, error: null };

		$scope.countFor = (key) => typed().filter((r) => r.job.status === key).length;
		$scope.overdueCount = () => typed().filter((r) => Store.isOverdue(r.job)).length;
		$scope.activeCount = () => typed().filter(isActive).length;
		$scope.collectedCount = () => typed().length - $scope.activeCount();
		$scope.typeCount = (key) => $scope.rows.filter((r) => key === "all" || typeOf(r) === key).length;
		// "At lab" is where a glasses job waits; a contact lens job at that stage has been dispatched.
		$scope.stageLabel = (s) => (s.key !== "IN_PRODUCTION" ? s.label : { glasses: "At lab", contacts: "Dispatched / at lab" }[$scope.filters.type] || "At lab / dispatched");

		const sortValue = {
			job: (row) => (row.job.created ? row.job.created.getTime() : 0),
			patient: (row) => $scope.patientName(row.patient).toLowerCase(),
			status: (row) => Store.statusIndex(row.job.status),
			date: (row) => {
				const d = Store.keyDate(row.job).date;
				return d ? new Date(d).getTime() : 0;
			},
			total: (row) => $scope.total(row.job),
		};

		function matchesSearch(row, text) {
			if (!text) return true;
			const job = row.job;
			return [
				Store.jobTitle(job),
				$scope.patientName(row.patient),
				Store.frameName(job.frame),
				job.lens && `${job.lens.supplier} ${job.lens.name}`,
				job.product && `${job.product.brand} ${job.product.name} contact lenses`,
				job.productOs && `${job.productOs.brand} ${job.productOs.name}`,
				job.order && job.order.ref,
				job.order && job.order.lab,
				job.invoice && job.invoice.number,
				(job.notes || []).map((n) => n.text).join(" "),
			].some((v) => v && v.toLowerCase().indexOf(text) !== -1);
		}

		function apply() {
			const f = $scope.filters;
			const text = (f.search || "").trim().toLowerCase();
			const value = sortValue[$scope.sort.key];
			$scope.visible = $scope.rows
				.filter((r) => (f.tab === "active" ? isActive(r) : !isActive(r)))
				.filter(ofType)
				.filter((r) => f.tab !== "active" || !f.status || r.job.status === f.status)
				.filter((r) => f.tab !== "active" || !f.overdue || Store.isOverdue(r.job))
				.filter((r) => matchesSearch(r, text))
				.sort((a, b) => {
					const va = value(a);
					const vb = value(b);
					const order = va < vb ? -1 : va > vb ? 1 : 0;
					// Ties fall back to the key date, oldest first, so the longest-waiting job leads.
					return ($scope.sort.reverse ? -order : order) || sortValue.date(a) - sortValue.date(b);
				});
		}

		function refresh() {
			$scope.state.error = null;
			$scope.state.loading = true;
			return Store.loadPracticeJobs()
				.then(function (rows) {
					$scope.rows = rows;
					apply();
				})
				.catch((resp) => ($scope.state.error = Store.errorMessage(resp, "The dispensing jobs could not be loaded.")))
				.finally(() => ($scope.state.loading = false));
		}
		$scope.reload = refresh;

		const fail = (resp, fallback) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, fallback), className: "alert-danger", dismissible: true });

		$scope.$watch("filters", apply, true);
		$scope.$watch("sort", apply, true);
		refresh();

		$scope.setTab = function (tab) {
			$scope.filters.tab = tab;
			$scope.filters.status = null;
			$scope.filters.overdue = false;
			$scope.sort = tab === "active" ? { key: "status", reverse: true } : { key: "date", reverse: true };
		};

		$scope.setType = function (key) {
			$scope.filters.type = key;
			$scope.filters.status = null;
			$scope.filters.overdue = false;
		};

		$scope.toggleStatus = function (key) {
			$scope.filters.overdue = false;
			$scope.filters.status = $scope.filters.status === key ? null : key;
		};

		$scope.toggleOverdue = function () {
			$scope.filters.status = null;
			$scope.filters.overdue = !$scope.filters.overdue;
		};

		$scope.sortBy = function (key) {
			if ($scope.sort.key === key) $scope.sort.reverse = !$scope.sort.reverse;
			else $scope.sort = { key: key, reverse: key === "date" && $scope.filters.tab !== "active" };
		};

		$scope.sortIcon = function (key) {
			if ($scope.sort.key !== key) return "fa-sort";
			return $scope.sort.reverse ? "fa-sort-desc" : "fa-sort-asc";
		};

		// Opens the job dialog on top of this one, with the patient read in full - the invoice is sent
		// to their email or mobile. The list is read again when it closes.
		$scope.openJob = function (row) {
			if (row.$opening) return;
			row.$opening = true;
			const dialog = Store.dialogFor(row.job);
			PatientAPI.getPatient(row.patient.id)
				.then((resp) => resp.data, () => row.patient)
				.then((patient) => $mdDialog.show({
					controller: dialog.controller,
					templateUrl: dialog.templateUrl,
					parent: angular.element(document.body),
					clickOutsideToClose: false,
					escapeToClose: false,
					multiple: true,
					scope: $scope.$new(true),
					preserveScope: false,
					locals: { existingJob: row.job, jobPatient: patient },
				}))
				.catch(angular.noop)
				.finally(function () {
					row.$opening = false;
					refresh();
				});
		};

		// Moves the job on. Marking the order sent asks for the supplier's reference first, and saves the
		// order PDF to the patient's files.
		$scope.advance = function (row, $event) {
			if ($event) $event.stopPropagation();
			const job = row.job;
			const action = Store.nextActionFor(job);
			if (!action || job.$busy) return;
			// Opens over the job list, which is itself a dialog.
			const confirmed = LensStatusDialog.needed(job, action) ? LensStatusDialog.show(job, row.patient, action, true) : $q.resolve({});
			confirmed.then(function (result) {
				// A payment taken in the dialog replaced the job.
				const current = result.job || job;
				row.job = current;
				current.$busy = true;
				Store.advanceJob(row.patient.id, current, { reference: result.reference || null })
					.then(function (saved) {
						row.job = saved;
						apply();
					})
					.catch((resp) => fail(resp, "The job could not be moved on."))
					.finally(() => (current.$busy = false));
			}, angular.noop);
		};

		// Puts a mis-clicked status back one step.
		$scope.undoStatus = Store.previousStatus;
		$scope.undoLabel = Store.undoLabel;
		$scope.undo = function (row, $event) {
			if ($event) $event.stopPropagation();
			const job = row.job;
			if (!Store.previousStatus(job) || job.$busy) return;
			job.$busy = true;
			Store.revertJob(row.patient.id, job)
				.then(function (saved) {
					row.job = saved;
					apply();
				})
				.catch((resp) => fail(resp, "The job could not be put back a step."))
				.finally(() => (job.$busy = false));
		};

		$scope.goToPatient = function (row, $event) {
			if ($event) $event.stopPropagation();
			const patient = row.patient;

			if (CurrentPatient.patient && CurrentPatient.patient.id === patient.id) {
				$mdDialog.hide();
				if ($state.current.name !== "patient-dashboard") $state.go("patient-dashboard");
				return;
			}

			$scope.loadingPatient = true;
			PatientAPI.getPatient(patient.id)
				.then(
					function (resp) {
						CurrentPatient.clearPatient();
						$timeout(function () {
							CurrentPatient.setPatient(resp.data);
							$mdDialog.hide();
							if ($state.current.name === "patient-dashboard") $state.reload();
							else $state.go("patient-dashboard");
						}, 50);
					},
					function () {
						toast.create({ timeout: 5000, message: "Couldn't open that patient. Please try again.", className: "alert-danger", dismissible: true });
					}
				)
				.finally(function () {
					$scope.loadingPatient = false;
				});
		};
	},
]);
